// bluaIQ — Finish Selections Wizard
// After AI takeoff, detects missing specs (finishes, fixtures, appliances)
// Walks user through selections room-by-room with price tiers
// Adds to takeoff + budget automatically
// If design is needed (kitchen without layout), generates RFQ to designer

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Sparkles, ChevronRight, ChevronLeft, Check, AlertTriangle,
  Bath, ChefHat, Footprints, Paintbrush, DoorOpen, Lightbulb,
  Home, Plus, X, Download, FileText, Send, Eye, Lock
} from 'lucide-react'
import ProFeatureGate from './ProFeatureGate'
import { downloadCsv } from '../utils/exportCsv'

// ── TYPES ─────────────────────────────────────────

interface SelectionItem {
  id: string
  category: string
  room: string
  item: string
  itemEs: string
  options: SelectionOption[]
  selectedOption?: string
  customNote?: string
  needsDesign?: boolean // true = no layout exists, needs designer RFQ
}

interface SelectionOption {
  id: string
  label: string
  labelEs: string
  priceRange: string // e.g. "$180-220"
  avgPrice: number
  tier: 'budget' | 'mid' | 'premium'
  description?: string
  descriptionEs?: string
}

interface CategoryConfig {
  key: string
  icon: typeof Bath
  en: string
  es: string
  color: string
  bg: string
}

// ── CATEGORIES ────────────────────────────────────

const CATEGORIES: CategoryConfig[] = [
  { key: 'BATHROOMS', icon: Bath, en: 'Bathrooms', es: 'Baños', color: '#3B82F6', bg: 'bg-blue-50' },
  { key: 'KITCHEN', icon: ChefHat, en: 'Kitchen', es: 'Cocina', color: '#F59E0B', bg: 'bg-amber-50' },
  { key: 'FLOORING', icon: Footprints, en: 'Flooring', es: 'Pisos', color: '#14B8A6', bg: 'bg-teal-50' },
  { key: 'PAINT', icon: Paintbrush, en: 'Paint', es: 'Pintura', color: '#EC4899', bg: 'bg-pink-50' },
  { key: 'DOORS', icon: DoorOpen, en: 'Doors & Hardware', es: 'Puertas y Herrajes', color: '#00BCD4', bg: 'bg-purple-50' },
  { key: 'LIGHTING', icon: Lightbulb, en: 'Lighting', es: 'Iluminación', color: '#EAB308', bg: 'bg-yellow-50' },
  { key: 'EXTERIOR', icon: Home, en: 'Exterior', es: 'Exterior', color: '#22C55E', bg: 'bg-green-50' },
]

// ── DEMO SELECTIONS (1,800 sqft spec, 3 bed / 2 bath) ──

const DEMO_SELECTIONS: SelectionItem[] = [
  // ─── BATHROOMS ───
  {
    id: 'fs-1', category: 'BATHROOMS', room: 'Master Bath', item: 'Toilet', itemEs: 'Sanitario',
    options: [
      { id: 'o1a', label: 'Standard Round Bowl', labelEs: 'Estándar redondo', priceRange: '$140-180', avgPrice: 160, tier: 'budget' },
      { id: 'o1b', label: 'Elongated Comfort Height', labelEs: 'Elongado altura cómoda', priceRange: '$220-280', avgPrice: 250, tier: 'mid', description: 'ADA-compliant height, most popular for master', descriptionEs: 'Altura ADA, más popular para master' },
      { id: 'o1c', label: 'One-Piece Skirted', labelEs: 'Una pieza con faldón', priceRange: '$350-450', avgPrice: 400, tier: 'premium', description: 'Modern look, easier to clean', descriptionEs: 'Look moderno, más fácil de limpiar' },
    ],
  },
  {
    id: 'fs-2', category: 'BATHROOMS', room: 'Master Bath', item: 'Vanity + Sink', itemEs: 'Tocador + Lavabo',
    options: [
      { id: 'o2a', label: '48" Single Sink Vanity', labelEs: 'Tocador 48" lavabo sencillo', priceRange: '$350-500', avgPrice: 420, tier: 'budget' },
      { id: 'o2b', label: '60" Double Sink Vanity', labelEs: 'Tocador 60" doble lavabo', priceRange: '$600-900', avgPrice: 750, tier: 'mid', description: 'Cultured marble top included', descriptionEs: 'Cubierta de mármol cultivado incluida' },
      { id: 'o2c', label: '72" Double Sink w/ Quartz', labelEs: 'Tocador 72" doble con Cuarzo', priceRange: '$1,200-1,800', avgPrice: 1500, tier: 'premium' },
    ],
  },
  {
    id: 'fs-3', category: 'BATHROOMS', room: 'Master Bath', item: 'Shower Faucet Set', itemEs: 'Juego de Regadera',
    options: [
      { id: 'o3a', label: 'Single Handle Chrome', labelEs: 'Monocomando cromo', priceRange: '$80-120', avgPrice: 100, tier: 'budget' },
      { id: 'o3b', label: 'Brushed Nickel w/ Rain Head', labelEs: 'Níquel cepillado con regadera lluvia', priceRange: '$180-280', avgPrice: 230, tier: 'mid' },
      { id: 'o3c', label: 'Matte Black Thermostatic', labelEs: 'Negro mate termostático', priceRange: '$350-500', avgPrice: 420, tier: 'premium' },
    ],
  },
  {
    id: 'fs-4', category: 'BATHROOMS', room: 'Master Bath', item: 'Mirror', itemEs: 'Espejo',
    options: [
      { id: 'o4a', label: 'Frameless 36x48"', labelEs: 'Sin marco 36x48"', priceRange: '$60-90', avgPrice: 75, tier: 'budget' },
      { id: 'o4b', label: 'Framed 36x48" Brushed Nickel', labelEs: 'Con marco 36x48" níquel', priceRange: '$120-180', avgPrice: 150, tier: 'mid' },
      { id: 'o4c', label: 'LED Backlit 36x48"', labelEs: 'LED retroiluminado 36x48"', priceRange: '$250-400', avgPrice: 320, tier: 'premium' },
    ],
  },
  {
    id: 'fs-5', category: 'BATHROOMS', room: 'Master Bath', item: 'Shower Walls', itemEs: 'Paredes de Regadera',
    options: [
      { id: 'o5a', label: 'Acrylic Surround (3-piece)', labelEs: 'Acrílico 3 piezas', priceRange: '$250-400', avgPrice: 320, tier: 'budget', description: 'Quick install, basic look', descriptionEs: 'Instalación rápida, look básico' },
      { id: 'o5b', label: 'Ceramic Tile 12x24"', labelEs: 'Cerámica 12x24"', priceRange: '$800-1,400', avgPrice: 1100, tier: 'mid', description: 'Includes labor + materials for ~50 sqft', descriptionEs: 'Incluye mano de obra + material ~50 sqft' },
      { id: 'o5c', label: 'Porcelain Tile + Accent Niche', labelEs: 'Porcelanato + Nicho de acento', priceRange: '$1,500-2,200', avgPrice: 1850, tier: 'premium' },
    ],
  },
  {
    id: 'fs-6', category: 'BATHROOMS', room: 'Hall Bath', item: 'Toilet', itemEs: 'Sanitario',
    options: [
      { id: 'o6a', label: 'Standard Round Bowl', labelEs: 'Estándar redondo', priceRange: '$140-180', avgPrice: 160, tier: 'budget' },
      { id: 'o6b', label: 'Elongated Standard Height', labelEs: 'Elongado altura estándar', priceRange: '$180-240', avgPrice: 210, tier: 'mid' },
      { id: 'o6c', label: 'Elongated Comfort Height', labelEs: 'Elongado altura cómoda', priceRange: '$220-280', avgPrice: 250, tier: 'premium' },
    ],
  },
  {
    id: 'fs-7', category: 'BATHROOMS', room: 'Hall Bath', item: 'Vanity + Sink', itemEs: 'Tocador + Lavabo',
    options: [
      { id: 'o7a', label: '30" Single Sink Basic', labelEs: 'Tocador 30" básico', priceRange: '$200-320', avgPrice: 260, tier: 'budget' },
      { id: 'o7b', label: '36" Single Sink w/ Marble Top', labelEs: 'Tocador 36" con mármol', priceRange: '$400-600', avgPrice: 500, tier: 'mid' },
      { id: 'o7c', label: '36" Floating Vanity w/ Quartz', labelEs: 'Tocador 36" flotante con cuarzo', priceRange: '$650-900', avgPrice: 775, tier: 'premium' },
    ],
  },
  {
    id: 'fs-8', category: 'BATHROOMS', room: 'Hall Bath', item: 'Tub/Shower Combo', itemEs: 'Tina/Regadera Combo',
    options: [
      { id: 'o8a', label: 'Fiberglass Tub + Surround', labelEs: 'Tina fibra + recubrimiento', priceRange: '$300-450', avgPrice: 370, tier: 'budget' },
      { id: 'o8b', label: 'Acrylic Tub + Tile Surround', labelEs: 'Tina acrílica + cerámica', priceRange: '$800-1,200', avgPrice: 1000, tier: 'mid' },
      { id: 'o8c', label: 'Cast Iron Tub + Porcelain Tile', labelEs: 'Tina hierro fundido + porcelanato', priceRange: '$1,500-2,000', avgPrice: 1750, tier: 'premium' },
    ],
  },
  {
    id: 'fs-9', category: 'BATHROOMS', room: 'Half Bath (Powder)', item: 'Toilet', itemEs: 'Sanitario',
    options: [
      { id: 'o9a', label: 'Standard Round Bowl', labelEs: 'Estándar redondo', priceRange: '$140-180', avgPrice: 160, tier: 'budget' },
      { id: 'o9b', label: 'Elongated Comfort Height', labelEs: 'Elongado altura cómoda', priceRange: '$220-280', avgPrice: 250, tier: 'mid' },
    ],
  },
  {
    id: 'fs-10', category: 'BATHROOMS', room: 'Half Bath (Powder)', item: 'Pedestal Sink or Small Vanity', itemEs: 'Lavabo Pedestal o Tocador Pequeño',
    options: [
      { id: 'o10a', label: 'Pedestal Sink White', labelEs: 'Lavabo pedestal blanco', priceRange: '$100-160', avgPrice: 130, tier: 'budget' },
      { id: 'o10b', label: '24" Wall-Mount Vanity', labelEs: 'Tocador 24" de pared', priceRange: '$250-400', avgPrice: 320, tier: 'mid' },
      { id: 'o10c', label: '24" Vessel Sink + Vanity', labelEs: 'Lavabo vessel + mueble 24"', priceRange: '$400-600', avgPrice: 500, tier: 'premium' },
    ],
  },

  // ─── KITCHEN ───
  {
    id: 'fs-20', category: 'KITCHEN', room: 'Kitchen', item: 'Countertop', itemEs: 'Cubierta',
    options: [
      { id: 'o20a', label: 'Laminate (Formica)', labelEs: 'Laminado (Formica)', priceRange: '$800-1,200', avgPrice: 1000, tier: 'budget', description: '~40 linear ft, basic colors', descriptionEs: '~40 pies lineales, colores básicos' },
      { id: 'o20b', label: 'Granite Level 1', labelEs: 'Granito Nivel 1', priceRange: '$2,000-3,000', avgPrice: 2500, tier: 'mid', description: 'Installed, basic edges, undermount cutout', descriptionEs: 'Instalado, bordes básicos, corte para tarja' },
      { id: 'o20c', label: 'Quartz (Cambria/Silestone)', labelEs: 'Cuarzo (Cambria/Silestone)', priceRange: '$3,500-5,500', avgPrice: 4500, tier: 'premium', description: 'Non-porous, no sealing needed', descriptionEs: 'No poroso, sin sellado' },
    ],
    needsDesign: false,
  },
  {
    id: 'fs-21', category: 'KITCHEN', room: 'Kitchen', item: 'Cabinets', itemEs: 'Gabinetes',
    options: [
      { id: 'o21a', label: 'Stock Cabinets (Builder Grade)', labelEs: 'Gabinetes stock (Builder Grade)', priceRange: '$3,000-5,000', avgPrice: 4000, tier: 'budget', description: 'RTA or semi-assembled, shaker white', descriptionEs: 'Pre-armados, estilo shaker blanco' },
      { id: 'o21b', label: 'Semi-Custom (Plywood Box)', labelEs: 'Semi-custom (caja madera)', priceRange: '$6,000-10,000', avgPrice: 8000, tier: 'mid', description: 'Soft-close, dovetail drawers', descriptionEs: 'Cierre suave, cajones cola de milano' },
      { id: 'o21c', label: 'Custom Cabinets', labelEs: 'Gabinetes a medida', priceRange: '$12,000-20,000', avgPrice: 16000, tier: 'premium', description: 'Requires kitchen design — will generate RFQ', descriptionEs: 'Requiere diseño de cocina — se generará cotización' },
    ],
    needsDesign: true,
  },
  {
    id: 'fs-22', category: 'KITCHEN', room: 'Kitchen', item: 'Sink + Faucet', itemEs: 'Tarja + Llave',
    options: [
      { id: 'o22a', label: 'SS Drop-In + Chrome Faucet', labelEs: 'Tarja sobreponer + llave cromo', priceRange: '$150-250', avgPrice: 200, tier: 'budget' },
      { id: 'o22b', label: 'SS Undermount + Pull-Down Faucet', labelEs: 'Tarja bajo cubierta + llave extraíble', priceRange: '$300-500', avgPrice: 400, tier: 'mid' },
      { id: 'o22c', label: 'Farmhouse Sink + Touchless Faucet', labelEs: 'Tarja tipo granja + llave sin contacto', priceRange: '$600-1,000', avgPrice: 800, tier: 'premium' },
    ],
  },
  {
    id: 'fs-23', category: 'KITCHEN', room: 'Kitchen', item: 'Backsplash', itemEs: 'Backsplash',
    options: [
      { id: 'o23a', label: 'None (paint only)', labelEs: 'Sin backsplash (solo pintura)', priceRange: '$0', avgPrice: 0, tier: 'budget' },
      { id: 'o23b', label: 'Subway Tile 3x6"', labelEs: 'Azulejo subway 3x6"', priceRange: '$400-700', avgPrice: 550, tier: 'mid', description: '~30 sqft behind range + sink', descriptionEs: '~30 sqft detrás de estufa + tarja' },
      { id: 'o23c', label: 'Full Height Tile to Ceiling', labelEs: 'Azulejo hasta el techo', priceRange: '$1,000-1,800', avgPrice: 1400, tier: 'premium' },
    ],
  },
  {
    id: 'fs-24', category: 'KITCHEN', room: 'Kitchen', item: 'Appliance Package', itemEs: 'Paquete de Electrodomésticos',
    options: [
      { id: 'o24a', label: 'Basic 4-pc (Range, Fridge, DW, Micro)', labelEs: 'Básico 4 pzas (estufa, refri, lava, micro)', priceRange: '$2,200-3,000', avgPrice: 2600, tier: 'budget', description: 'White or black, standard brands', descriptionEs: 'Blanco o negro, marcas estándar' },
      { id: 'o24b', label: 'Stainless Steel 4-pc Package', labelEs: 'Acero inoxidable 4 pzas', priceRange: '$3,500-5,000', avgPrice: 4200, tier: 'mid', description: 'Fingerprint-resistant SS', descriptionEs: 'Acero inox anti-huellas' },
      { id: 'o24c', label: 'Premium SS (Samsung/LG/GE Profile)', labelEs: 'Premium SS (Samsung/LG/GE Profile)', priceRange: '$5,500-8,000', avgPrice: 6800, tier: 'premium' },
    ],
  },

  // ─── FLOORING ───
  {
    id: 'fs-30', category: 'FLOORING', room: 'Living Areas (~900 sqft)', item: 'Main Floor', itemEs: 'Piso Principal',
    options: [
      { id: 'o30a', label: 'LVP Basic (5mm)', labelEs: 'LVP básico (5mm)', priceRange: '$2.50-3.50/sqft', avgPrice: 2700, tier: 'budget', description: '~900 sqft installed', descriptionEs: '~900 sqft instalado' },
      { id: 'o30b', label: 'LVP Premium (7mm w/ pad)', labelEs: 'LVP premium (7mm con base)', priceRange: '$4.00-5.50/sqft', avgPrice: 4300, tier: 'mid', description: 'Waterproof, realistic wood look', descriptionEs: 'Resistente al agua, apariencia de madera' },
      { id: 'o30c', label: 'Engineered Hardwood', labelEs: 'Duela de ingeniería', priceRange: '$6.50-9.00/sqft', avgPrice: 7000, tier: 'premium', description: 'Real wood top layer, 20+ year life', descriptionEs: 'Capa superior de madera real, 20+ años' },
    ],
  },
  {
    id: 'fs-31', category: 'FLOORING', room: 'Bedrooms (~500 sqft)', item: 'Bedroom Floor', itemEs: 'Piso de Recámaras',
    options: [
      { id: 'o31a', label: 'Carpet Builder Grade', labelEs: 'Alfombra builder grade', priceRange: '$1.50-2.50/sqft', avgPrice: 1000, tier: 'budget', description: '~500 sqft w/ pad, basic colors', descriptionEs: '~500 sqft con base, colores básicos' },
      { id: 'o31b', label: 'Carpet Mid-Grade (StainMaster)', labelEs: 'Alfombra media (StainMaster)', priceRange: '$3.00-4.50/sqft', avgPrice: 1875, tier: 'mid' },
      { id: 'o31c', label: 'Same as Living (LVP/Hardwood)', labelEs: 'Mismo que sala (LVP/duela)', priceRange: '$4.00-9.00/sqft', avgPrice: 3250, tier: 'premium', description: 'Continuous floor throughout', descriptionEs: 'Piso continuo en toda la casa' },
    ],
  },
  {
    id: 'fs-32', category: 'FLOORING', room: 'All Rooms', item: 'Baseboards', itemEs: 'Zoclos / Molduras',
    options: [
      { id: 'o32a', label: '3.25" MDF Primed Baseboards', labelEs: 'Zoclo MDF 3.25" preparado', priceRange: '$0.80-1.20/lf', avgPrice: 350, tier: 'budget', description: '~350 linear ft for 1,800 sqft home', descriptionEs: '~350 pies lineales para casa 1,800 sqft' },
      { id: 'o32b', label: '5.25" MDF Baseboards', labelEs: 'Zoclo MDF 5.25"', priceRange: '$1.50-2.50/lf', avgPrice: 700, tier: 'mid' },
      { id: 'o32c', label: '5.25" Solid Pine + Crown Molding', labelEs: 'Zoclo pino 5.25" + moldura corona', priceRange: '$3.00-5.00/lf', avgPrice: 1400, tier: 'premium' },
    ],
  },

  // ─── PAINT ───
  {
    id: 'fs-40', category: 'PAINT', room: 'Interior (~4,800 sqft walls)', item: 'Interior Paint', itemEs: 'Pintura Interior',
    options: [
      { id: 'o40a', label: 'Flat White (1 color throughout)', labelEs: 'Mate blanco (1 color)', priceRange: '$1,800-2,500', avgPrice: 2150, tier: 'budget', description: 'Walls + ceiling, builder white', descriptionEs: 'Paredes + techo, blanco builder' },
      { id: 'o40b', label: 'Eggshell (2-3 colors)', labelEs: 'Cáscara de huevo (2-3 colores)', priceRange: '$3,000-4,000', avgPrice: 3500, tier: 'mid', description: 'More durable, accent walls', descriptionEs: 'Más durable, paredes de acento' },
      { id: 'o40c', label: 'Premium (Sherwin-Williams, 3+ colors)', labelEs: 'Premium (Sherwin-Williams, 3+ colores)', priceRange: '$4,500-6,000', avgPrice: 5250, tier: 'premium' },
    ],
  },
  {
    id: 'fs-41', category: 'PAINT', room: 'Exterior', item: 'Exterior Paint', itemEs: 'Pintura Exterior',
    options: [
      { id: 'o41a', label: '1 Body Color + White Trim', labelEs: '1 color + molduras blancas', priceRange: '$2,500-3,500', avgPrice: 3000, tier: 'budget' },
      { id: 'o41b', label: '2 Colors + Accent + Trim', labelEs: '2 colores + acento + molduras', priceRange: '$3,500-5,000', avgPrice: 4250, tier: 'mid' },
      { id: 'o41c', label: '3+ Colors + Shutters + Details', labelEs: '3+ colores + postigos + detalles', priceRange: '$5,000-7,000', avgPrice: 6000, tier: 'premium' },
    ],
  },

  // ─── DOORS ───
  {
    id: 'fs-50', category: 'DOORS', room: 'Interior (12 doors)', item: 'Interior Doors', itemEs: 'Puertas Interiores',
    options: [
      { id: 'o50a', label: 'Hollow Core 6-Panel (12 doors)', labelEs: 'Huecas 6 paneles (12 puertas)', priceRange: '$600-900', avgPrice: 750, tier: 'budget', description: '$50-75 each, basic white', descriptionEs: '$50-75 c/u, blanco básico' },
      { id: 'o50b', label: 'Hollow Core Shaker (12 doors)', labelEs: 'Huecas Shaker (12 puertas)', priceRange: '$960-1,440', avgPrice: 1200, tier: 'mid', description: '$80-120 each, modern look', descriptionEs: '$80-120 c/u, look moderno' },
      { id: 'o50c', label: 'Solid Core Shaker (12 doors)', labelEs: 'Sólidas Shaker (12 puertas)', priceRange: '$1,800-2,400', avgPrice: 2100, tier: 'premium', description: '$150-200 each, sound dampening', descriptionEs: '$150-200 c/u, aislamiento acústico' },
    ],
  },
  {
    id: 'fs-51', category: 'DOORS', room: 'Interior', item: 'Door Hardware (knobs/levers)', itemEs: 'Herrajes (perillas/manijas)',
    options: [
      { id: 'o51a', label: 'Chrome Knobs (12 + 2 privacy)', labelEs: 'Perillas cromo (12 + 2 privacidad)', priceRange: '$180-240', avgPrice: 210, tier: 'budget' },
      { id: 'o51b', label: 'Brushed Nickel Levers (12 + 2)', labelEs: 'Manijas níquel (12 + 2)', priceRange: '$280-400', avgPrice: 340, tier: 'mid' },
      { id: 'o51c', label: 'Matte Black Levers (12 + 2)', labelEs: 'Manijas negro mate (12 + 2)', priceRange: '$360-500', avgPrice: 430, tier: 'premium' },
    ],
  },
  {
    id: 'fs-52', category: 'DOORS', room: 'Closets', item: 'Closet Doors', itemEs: 'Puertas de Closet',
    options: [
      { id: 'o52a', label: 'Bifold White (5 openings)', labelEs: 'Bifold blancas (5 aberturas)', priceRange: '$350-500', avgPrice: 425, tier: 'budget' },
      { id: 'o52b', label: 'Sliding Bypass (5 openings)', labelEs: 'Corredizas bypass (5 aberturas)', priceRange: '$500-750', avgPrice: 625, tier: 'mid' },
      { id: 'o52c', label: 'Shaker Bifold (5 openings)', labelEs: 'Bifold Shaker (5 aberturas)', priceRange: '$700-1,000', avgPrice: 850, tier: 'premium' },
    ],
  },
  {
    id: 'fs-53', category: 'DOORS', room: 'Entry', item: 'Front Door', itemEs: 'Puerta Principal',
    options: [
      { id: 'o53a', label: 'Steel 6-Panel w/ Deadbolt', labelEs: 'Acero 6 paneles + cerrojo', priceRange: '$300-500', avgPrice: 400, tier: 'budget' },
      { id: 'o53b', label: 'Fiberglass w/ Decorative Glass', labelEs: 'Fibra de vidrio con vitral', priceRange: '$600-1,000', avgPrice: 800, tier: 'mid' },
      { id: 'o53c', label: 'Fiberglass Craftsman + Smart Lock', labelEs: 'Craftsman fibra + cerradura inteligente', priceRange: '$1,200-2,000', avgPrice: 1600, tier: 'premium' },
    ],
  },

  // ─── LIGHTING ───
  {
    id: 'fs-60', category: 'LIGHTING', room: 'Throughout', item: 'Recessed Cans (LED)', itemEs: 'Botes empotrados (LED)',
    options: [
      { id: 'o60a', label: '18 cans — Basic 4" LED', labelEs: '18 botes — LED 4" básicos', priceRange: '$540-720', avgPrice: 630, tier: 'budget', description: '$30-40 each installed', descriptionEs: '$30-40 c/u instalado' },
      { id: 'o60b', label: '24 cans — 4" LED Gimbal', labelEs: '24 botes — LED 4" gimbal', priceRange: '$960-1,200', avgPrice: 1080, tier: 'mid', description: 'Adjustable direction, dimmable', descriptionEs: 'Dirección ajustable, dimeables' },
      { id: 'o60c', label: '30 cans — 4" LED + Undercab', labelEs: '30 botes — LED 4" + bajo gabinete', priceRange: '$1,500-2,000', avgPrice: 1750, tier: 'premium' },
    ],
  },
  {
    id: 'fs-61', category: 'LIGHTING', room: 'Key Rooms', item: 'Light Fixtures (non-recessed)', itemEs: 'Luminarias decorativas',
    options: [
      { id: 'o61a', label: 'Builder Pack (8 fixtures)', labelEs: 'Paquete builder (8 luminarias)', priceRange: '$400-600', avgPrice: 500, tier: 'budget', description: 'Entry, dining, kitchen, baths, bedrooms', descriptionEs: 'Entrada, comedor, cocina, baños, recámaras' },
      { id: 'o61b', label: 'Mid-Grade Mix (8 fixtures)', labelEs: 'Mezcla media (8 luminarias)', priceRange: '$800-1,200', avgPrice: 1000, tier: 'mid' },
      { id: 'o61c', label: 'Designer Mix (8 fixtures)', labelEs: 'Mezcla diseñador (8 luminarias)', priceRange: '$1,500-2,500', avgPrice: 2000, tier: 'premium' },
    ],
  },

  // ─── EXTERIOR ───
  {
    id: 'fs-70', category: 'EXTERIOR', room: 'Exterior', item: 'Garage Door', itemEs: 'Puerta de Garage',
    options: [
      { id: 'o70a', label: 'Steel Non-Insulated 16x7', labelEs: 'Acero sin aislamiento 16x7', priceRange: '$600-900', avgPrice: 750, tier: 'budget' },
      { id: 'o70b', label: 'Steel Insulated 16x7 + Opener', labelEs: 'Acero aislado 16x7 + motor', priceRange: '$1,200-1,800', avgPrice: 1500, tier: 'mid' },
      { id: 'o70c', label: 'Carriage Style Insulated + Smart Opener', labelEs: 'Estilo carruaje aislado + motor inteligente', priceRange: '$2,000-3,000', avgPrice: 2500, tier: 'premium' },
    ],
  },
  {
    id: 'fs-71', category: 'EXTERIOR', room: 'Exterior', item: 'Mailbox', itemEs: 'Buzón',
    options: [
      { id: 'o71a', label: 'Standard Post Mount', labelEs: 'Estándar de poste', priceRange: '$30-60', avgPrice: 45, tier: 'budget' },
      { id: 'o71b', label: 'Column Mount Brick/Stone', labelEs: 'Columna ladrillo/piedra', priceRange: '$200-400', avgPrice: 300, tier: 'mid' },
      { id: 'o71c', label: 'Locking Column w/ Package Box', labelEs: 'Columna con cerradura + caja paquetes', priceRange: '$400-700', avgPrice: 550, tier: 'premium' },
    ],
  },
  {
    id: 'fs-72', category: 'EXTERIOR', room: 'Yard', item: 'Landscaping', itemEs: 'Jardinería / Paisajismo',
    options: [
      { id: 'o72a', label: 'Sod + Basic Beds + 3 Trees', labelEs: 'Pasto + arriates + 3 árboles', priceRange: '$3,000-5,000', avgPrice: 4000, tier: 'budget' },
      { id: 'o72b', label: 'Sod + Beds + Irrigation + 5 Trees', labelEs: 'Pasto + arriates + riego + 5 árboles', priceRange: '$6,000-9,000', avgPrice: 7500, tier: 'mid' },
      { id: 'o72c', label: 'Full Landscape Design + Install', labelEs: 'Diseño completo + instalación', priceRange: '$10,000-18,000', avgPrice: 14000, tier: 'premium', description: 'Requires landscape design — will generate RFQ', descriptionEs: 'Requiere diseño de paisajismo — se generará cotización' },
    ],
    needsDesign: true,
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function FinishSelectionsWizard() {
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  const [selections, setSelections] = useState<SelectionItem[]>(DEMO_SELECTIONS)
  const [activeCategory, setActiveCategory] = useState('BATHROOMS')
  const [showSummary, setShowSummary] = useState(false)
  const [tierPreset, setTierPreset] = useState<'budget' | 'mid' | 'premium' | null>(null)

  // Filter items by active category
  const categoryItems = selections.filter(s => s.category === activeCategory)

  // Group by room
  const rooms = [...new Set(categoryItems.map(s => s.room))]

  // Select an option for an item
  const selectOption = (itemId: string, optionId: string) => {
    setSelections(prev => prev.map(s =>
      s.id === itemId ? { ...s, selectedOption: optionId } : s
    ))
  }

  // Apply tier preset to all items in a category
  const applyTierPreset = (tier: 'budget' | 'mid' | 'premium') => {
    setTierPreset(tier)
    setSelections(prev => prev.map(s => {
      if (s.category === activeCategory) {
        const option = s.options.find(o => o.tier === tier) || s.options[0]
        return { ...s, selectedOption: option.id }
      }
      return s
    }))
  }

  // Apply tier to ALL categories
  const applyTierAll = (tier: 'budget' | 'mid' | 'premium') => {
    setSelections(prev => prev.map(s => {
      const option = s.options.find(o => o.tier === tier) || s.options[0]
      return { ...s, selectedOption: option.id }
    }))
  }

  // Calculate totals
  const getTotal = () => {
    let total = 0
    selections.forEach(s => {
      if (s.selectedOption) {
        const opt = s.options.find(o => o.id === s.selectedOption)
        if (opt) total += opt.avgPrice
      }
    })
    return total
  }

  const getSelectedCount = () => selections.filter(s => s.selectedOption).length
  const getMissingCount = () => selections.filter(s => !s.selectedOption).length
  const getNeedsDesign = () => selections.filter(s => {
    if (!s.selectedOption || !s.needsDesign) return false
    const opt = s.options.find(o => o.id === s.selectedOption)
    return opt?.tier === 'premium'
  })

  // Category progress
  const getCategoryProgress = (cat: string) => {
    const items = selections.filter(s => s.category === cat)
    const selected = items.filter(s => s.selectedOption).length
    return { selected, total: items.length }
  }

  const getCategoryTotal = (cat: string) => {
    let total = 0
    selections.filter(s => s.category === cat).forEach(s => {
      if (s.selectedOption) {
        const opt = s.options.find(o => o.id === s.selectedOption)
        if (opt) total += opt.avgPrice
      }
    })
    return total
  }

  // Export
  const exportSelections = () => {
    const rows = selections.filter(s => s.selectedOption).map(s => {
      const opt = s.options.find(o => o.id === s.selectedOption)!
      return [
        s.category, s.room, isEs ? s.itemEs : s.item,
        isEs ? opt.labelEs : opt.label, opt.tier, opt.priceRange, `$${opt.avgPrice.toLocaleString()}`
      ]
    })
    downloadCsv({
      filename: `finish-selections-${projectId}`,
      headers: [
        isEs ? 'Categoría' : 'Category', isEs ? 'Cuarto' : 'Room', 'Item',
        isEs ? 'Selección' : 'Selection', 'Tier', isEs ? 'Rango' : 'Range', isEs ? 'Precio Est.' : 'Est. Price',
      ],
      rows,
    })
  }

  if (showSummary) {
    return (
      <FinishSummary
        selections={selections}
        isEs={isEs}
        total={getTotal()}
        needsDesign={getNeedsDesign()}
        onBack={() => setShowSummary(false)}
        onExport={exportSelections}
      />
    )
  }

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <Sparkles size={18} className="text-[#06539B]" />
            {isEs ? 'Selección de Acabados' : 'Finish Selections'}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {isEs
              ? `${getSelectedCount()}/${selections.length} seleccionados · Faltan ${getMissingCount()} · Estimado: $${getTotal().toLocaleString()}`
              : `${getSelectedCount()}/${selections.length} selected · ${getMissingCount()} missing · Est: $${getTotal().toLocaleString()}`
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportSelections}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB] transition-colors"
          >
            <Download size={12} /> CSV
          </button>
          <button
            onClick={() => setShowSummary(true)}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full"
          >
            <Eye size={14} /> {isEs ? 'Resumen' : 'Summary'}
          </button>
        </div>
      </div>

      {/* AI detected missing warning */}
      {getMissingCount() > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
          <div>
            <div className="text-[11px] font-[800] text-amber-700">
              {isEs
                ? `${getMissingCount()} partidas sin especificar en tus planos`
                : `${getMissingCount()} items not specified in your plans`
              }
            </div>
            <div className="text-[10px] font-[600] text-amber-600">
              {isEs
                ? 'Selecciona acabados para cada partida para tener un presupuesto completo.'
                : 'Select finishes for each item to get a complete budget.'
              }
            </div>
          </div>
        </div>
      )}

      {/* Quick tier presets */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
        <div className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">
          {isEs ? 'Aplicar nivel a todos' : 'Apply tier to all'}
        </div>
        <div className="flex gap-2">
          {(['budget', 'mid', 'premium'] as const).map(tier => (
            <button
              key={tier}
              onClick={() => applyTierAll(tier)}
              className={`flex-1 text-[13px] font-[800] py-2.5 rounded-full border transition-colors ${
                tier === 'budget' ? 'border-green-300 text-green-700 hover:bg-green-50' :
                tier === 'mid' ? 'border-blue-300 text-blue-700 hover:bg-blue-50' :
                'border-purple-300 text-purple-700 hover:bg-purple-50'
              }`}
            >
              {tier === 'budget' ? (isEs ? '💰 Económico' : '💰 Budget') :
               tier === 'mid' ? (isEs ? '⚖️ Medio' : '⚖️ Mid-Range') :
               (isEs ? '✨ Premium' : '✨ Premium')}
            </button>
          ))}
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {CATEGORIES.map(cat => {
          const progress = getCategoryProgress(cat.key)
          const isActive = activeCategory === cat.key
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                isActive
                  ? 'bg-[#06539B] text-white border-[#06539B]'
                  : 'bg-white border-[#ECEAE4] text-[#9E9890] hover:border-[#06539B]'
              }`}
            >
              <cat.icon size={14} />
              <span className="text-[11px] font-[800]">{isEs ? cat.es : cat.en}</span>
              <span className={`text-[9px] font-[800] px-1.5 py-0.5 rounded-full ${
                progress.selected === progress.total
                  ? (isActive ? 'bg-green-500 text-white' : 'bg-green-100 text-green-600')
                  : (isActive ? 'bg-white/20 text-white' : 'bg-[#F2F0EB] text-[#9E9890]')
              }`}>
                {progress.selected}/{progress.total}
              </span>
            </button>
          )
        })}
      </div>

      {/* Category tier preset */}
      <div className="flex items-center gap-3">
        <span className="text-[12px] font-[700] text-[#9E9890]">
          {isEs ? 'Esta categoría:' : 'This category:'}
        </span>
        {(['budget', 'mid', 'premium'] as const).map(tier => (
          <button
            key={tier}
            onClick={() => applyTierPreset(tier)}
            className="text-[9px] font-[800] px-2.5 py-1 rounded-full border border-[#ECEAE4] text-[#9E9890] hover:border-[#06539B] hover:text-[#1C1A17] transition-colors"
          >
            {tier === 'budget' ? '💰' : tier === 'mid' ? '⚖️' : '✨'} {tier}
          </button>
        ))}
        <span className="ml-auto text-[12px] font-[800] text-[#1C1A17]">
          ${getCategoryTotal(activeCategory).toLocaleString()}
        </span>
      </div>

      {/* Selection items grouped by room */}
      <div className="space-y-4">
        {rooms.map(room => (
          <div key={room}>
            <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2 flex items-center gap-2">
              {room}
              {categoryItems.filter(i => i.room === room && i.selectedOption).length === categoryItems.filter(i => i.room === room).length && (
                <Check size={12} className="text-[#22C55E]" />
              )}
            </h3>
            <div className="space-y-2">
              {categoryItems.filter(i => i.room === room).map(item => (
                <SelectionCard
                  key={item.id}
                  item={item}
                  isEs={isEs}
                  onSelect={(optId) => selectOption(item.id, optId)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Running total bar */}
      <div className="sticky bottom-4 bg-[#06539B] text-white rounded-2xl p-4 flex items-center justify-between shadow-xl shadow-black/20">
        <div>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">
            {isEs ? 'Total acabados estimado' : 'Estimated finish total'}
          </div>
          <div className="text-[24px] font-[900] tracking-[-1px]">${getTotal().toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-[700] text-white/50">
            {getSelectedCount()}/{selections.length} {isEs ? 'seleccionados' : 'selected'}
          </div>
          <button onClick={() => setShowSummary(true)}
            className="mt-1 bg-[#06539B] text-white text-[13px] font-[800] px-5 py-2 rounded-full hover:bg-[#14457E] transition-colors">
            {isEs ? 'Ver resumen →' : 'View summary →'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── SELECTION CARD ─────────────────────────────────

function SelectionCard({ item, isEs, onSelect }: {
  item: SelectionItem; isEs: boolean; onSelect: (optId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const selectedOpt = item.options.find(o => o.id === item.selectedOption)

  const tierColors = {
    budget: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', ring: 'ring-green-400' },
    mid: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', ring: 'ring-blue-400' },
    premium: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', ring: 'ring-purple-400' },
  }

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all ${
      selectedOpt ? 'border-[#22C55E]/40' : 'border-[#ECEAE4]'
    }`}>
      {/* Item header */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
      >
        <div className={`w-[8px] h-[8px] rounded-full flex-shrink-0 ${selectedOpt ? 'bg-[#22C55E]' : 'bg-[#ECEAE4]'}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-[800] text-[#1C1A17]">{isEs ? item.itemEs : item.item}</div>
          {selectedOpt && (
            <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5 truncate">
              {isEs ? selectedOpt.labelEs : selectedOpt.label} — <span className="font-[800] text-[#1C1A17]">${selectedOpt.avgPrice.toLocaleString()}</span>
            </div>
          )}
        </div>
        {selectedOpt && (
          <span className={`text-[8px] font-[800] px-2 py-0.5 rounded-full uppercase ${tierColors[selectedOpt.tier].bg} ${tierColors[selectedOpt.tier].text}`}>
            {selectedOpt.tier}
          </span>
        )}
        {!selectedOpt && (
          <span className="text-[9px] font-[700] text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">
            {isEs ? 'Pendiente' : 'Pending'}
          </span>
        )}
        <ChevronRight size={14} className={`text-[#9E9890] transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
      </div>

      {/* Options */}
      {expanded && (
        <div className="px-3 pb-3 space-y-1.5 border-t border-[#F2F0EB] pt-2">
          {item.options.map(opt => {
            const isSelected = item.selectedOption === opt.id
            const tc = tierColors[opt.tier]
            return (
              <button
                key={opt.id}
                onClick={() => onSelect(opt.id)}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? `${tc.bg} ${tc.border} ring-2 ${tc.ring}/20`
                    : 'border-[#F2F0EB] hover:border-[#ECEAE4] bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-[800] ${isSelected ? tc.text : 'text-[#1C1A17]'}`}>
                        {isEs ? opt.labelEs : opt.label}
                      </span>
                      <span className={`text-[8px] font-[800] px-1.5 py-0.5 rounded-full uppercase ${tc.bg} ${tc.text}`}>
                        {opt.tier === 'budget' ? '💰' : opt.tier === 'mid' ? '⚖️' : '✨'} {opt.tier}
                      </span>
                    </div>
                    {(opt.description || opt.descriptionEs) && (
                      <p className="text-[10px] font-[500] text-[#9E9890] mt-0.5">
                        {isEs ? opt.descriptionEs : opt.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[13px] font-[900] text-[#1C1A17]">${opt.avgPrice.toLocaleString()}</div>
                    <div className="text-[9px] font-[600] text-[#9E9890]">{opt.priceRange}</div>
                  </div>
                </div>
                {isSelected && (
                  <div className={`mt-1.5 flex items-center gap-1 text-[9px] font-[700] ${tc.text}`}>
                    <Check size={10} /> {isEs ? 'Seleccionado' : 'Selected'}
                  </div>
                )}
              </button>
            )
          })}

          {item.needsDesign && (
            <div className="flex items-center gap-2 bg-[#06539B]/5 border border-[#06539B]/20 rounded-lg p-2.5 mt-1">
              <FileText size={12} className="text-[#06539B] flex-shrink-0" />
              <span className="text-[10px] font-[700] text-[#06539B]">
                {isEs
                  ? 'Si eliges premium, se generará una solicitud de diseño automáticamente.'
                  : 'If you choose premium, a design request will be generated automatically.'
                }
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── SUMMARY VIEW ──────────────────────────────────

function FinishSummary({ selections, isEs, total, needsDesign, onBack, onExport }: {
  selections: SelectionItem[]
  isEs: boolean
  total: number
  needsDesign: SelectionItem[]
  onBack: () => void
  onExport: () => void
}) {
  const { t } = useTranslation()

  // Group by category
  const byCategory = CATEGORIES.map(cat => ({
    ...cat,
    items: selections.filter(s => s.category === cat.key && s.selectedOption),
    total: selections.filter(s => s.category === cat.key).reduce((sum, s) => {
      if (!s.selectedOption) return sum
      const opt = s.options.find(o => o.id === s.selectedOption)
      return sum + (opt?.avgPrice || 0)
    }, 0),
    missing: selections.filter(s => s.category === cat.key && !s.selectedOption).length,
  }))

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="flex items-center gap-1.5 text-[11px] font-[700] text-[#9E9890] hover:text-[#1C1A17] mb-1">
            <ChevronLeft size={14} /> {isEs ? 'Volver a selecciones' : 'Back to selections'}
          </button>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {isEs ? 'Resumen de Acabados' : 'Finish Selections Summary'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onExport}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB]">
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {/* Grand total */}
      <div className="bg-[#06539B] text-white rounded-2xl p-6">
        <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50 mb-1">
          {isEs ? 'Total estimado de acabados' : 'Estimated finish total'}
        </div>
        <div className="text-[36px] font-[900] tracking-[-2px]">${total.toLocaleString()}</div>
        <div className="text-[11px] font-[600] text-white/50 mt-1">
          {isEs
            ? 'Este monto se suma al presupuesto de construcción (estructura, cimentación, techo, etc.)'
            : 'This amount adds to your construction budget (framing, foundation, roofing, etc.)'
          }
        </div>
      </div>

      {/* Category breakdown */}
      <div className="space-y-3">
        {byCategory.map(cat => (
          <div key={cat.key} className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 p-4 border-b border-[#F2F0EB]">
              <div className={`w-[36px] h-[36px] rounded-xl ${cat.bg} flex items-center justify-center`}>
                <cat.icon size={18} style={{ color: cat.color }} />
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-[800] text-[#1C1A17]">{isEs ? cat.es : cat.en}</div>
                <div className="text-[10px] font-[600] text-[#9E9890]">
                  {cat.items.length} {isEs ? 'partidas seleccionadas' : 'items selected'}
                  {cat.missing > 0 && (
                    <span className="text-amber-500 ml-2">
                      · {cat.missing} {isEs ? 'pendientes' : 'pending'}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[16px] font-[900] text-[#1C1A17]">${cat.total.toLocaleString()}</span>
            </div>
            <div className="divide-y divide-[#F2F0EB]">
              {cat.items.map(item => {
                const opt = item.options.find(o => o.id === item.selectedOption)!
                return (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-[700] text-[#1C1A17]">{item.room} — {isEs ? item.itemEs : item.item}</div>
                      <div className="text-[10px] font-[500] text-[#9E9890] truncate">{isEs ? opt.labelEs : opt.label}</div>
                    </div>
                    <span className={`text-[8px] font-[800] px-1.5 py-0.5 rounded-full uppercase ${
                      opt.tier === 'budget' ? 'bg-green-50 text-green-600' :
                      opt.tier === 'mid' ? 'bg-blue-50 text-blue-600' :
                      'bg-purple-50 text-purple-600'
                    }`}>{opt.tier}</span>
                    <span className="text-[12px] font-[800] text-[#1C1A17] w-[70px] text-right">${opt.avgPrice.toLocaleString()}</span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Design RFQ needed */}
      {needsDesign.length > 0 && (
        <div className="bg-[#06539B]/5 border border-[#06539B]/20 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#06539B]" />
            <h3 className="text-[13px] font-[800] text-[#06539B]">
              {isEs ? 'Diseño necesario' : 'Design needed'}
            </h3>
          </div>
          <p className="text-[11px] font-[600] text-[#9E9890]">
            {isEs
              ? 'Las siguientes partidas requieren un diseño profesional antes de poder cotizar e instalar:'
              : 'The following items require professional design before quoting and installing:'
            }
          </p>
          <div className="space-y-1.5">
            {needsDesign.map(item => (
              <div key={item.id} className="flex items-center gap-2 text-[11px]">
                <span className="w-[6px] h-[6px] rounded-full bg-[#06539B]" />
                <span className="font-[700]">{item.room}</span>
                <span className="text-[#9E9890]">—</span>
                <span className="font-[600] text-[#9E9890]">{isEs ? item.itemEs : item.item}</span>
              </div>
            ))}
          </div>
          <button className="w-full flex items-center justify-center gap-2 bg-[#06539B] text-white text-[13px] font-[800] py-2.5 rounded-full hover:bg-[#14457E] transition-colors">
            <Send size={14} />
            {isEs ? 'Generar solicitud de diseño' : 'Generate design request'}
          </button>
        </div>
      )}

      {/* Add to budget button */}
      <button className="w-full bg-[#22C55E] text-white text-[14px] font-[800] py-4 rounded-full hover:bg-[#16A34A] transition-colors shadow-lg shadow-[#22C55E]/20 flex items-center justify-center gap-2">
        <Plus size={18} />
        {isEs
          ? `Agregar $${total.toLocaleString()} al presupuesto`
          : `Add $${total.toLocaleString()} to budget`
        }
      </button>
    </div>
  )
}
